import React, { useEffect, useState } from 'react';
import ym from 'react-yandex-metrika';

import { hrLight } from '@edu-frontend/modules/src/components/ui/Theme';
import { GptChatPopupWithAction } from '@edu-frontend/modules/src/widgets/gptChat';

import { useMe } from '../../../../common';
import { useCodingPropsSelector } from '../context';
import { useCodingStore } from '../state';

interface Props {
    anchor: React.ComponentProps<typeof GptChatPopupWithAction>['anchor'];

    problemId: number;
    resizeKey?: number;
}

export function CodingProblemGptPopup({ anchor, problemId, resizeKey }: Props) {
    const kelvinUserId = useMe('id');

    const gptDebugCode = useCodingPropsSelector((p) => p.gptDebugCode);

    const help = useCodingStore((s) => s.help)[problemId];
    const gptPopupVisible = useCodingStore((s) => s.gptPopupVisible);
    const setGptPopupVisible = useCodingStore((s) => s.setGptPopupVisible);

    // Ререндерим попап при обновлении help, иначе он пропадает
    const [rerenderKey, setRerenderKey] = useState(0);
    useEffect(() => setRerenderKey((v) => v + 1), [help]);

    // Ререндерим попап при переходе на другую карточку
    // Здесь не подошел rerenderKey, потому что анимация перехода
    // дублирует попапы – пока старый улетает вместе с предыдущей карточкой,
    // новый уже показывается
    const [hidePopup, setHidePopup] = useState(false);
    useEffect(() => {
        setHidePopup(true);
        const timeout = setTimeout(() => setHidePopup(false), 500);

        return () => {
            setHidePopup(false);
            clearTimeout(timeout);
        };
    }, [problemId]);

    const visible = !gptDebugCode || !help || !gptPopupVisible || hidePopup;

    useEffect(() => {
        if (visible) {
            ym('reachGoal', 'ai_debug_popup_view', {
                user_id: kelvinUserId,
                problem_id: problemId,
                user_code: help?.code,
                user_input: help?.input,
                error_message: help?.error,
            });
        }
    }, [visible, help, problemId, kelvinUserId]);

    if (visible) {
        return null;
    }

    const handleClose = () => setGptPopupVisible(false);

    const handleButtonClick = () => {
        ym('reachGoal', 'ai_debug_popup_button_click', {
            user_id: kelvinUserId,
            problem_id: problemId,
            user_code: help?.code,
            user_input: help?.input,
            error_message: help?.error,
        });

        gptDebugCode(help);
        handleClose();
    };

    const key = `${rerenderKey} ${resizeKey}`;

    return (
        <GptChatPopupWithAction
            anchor={anchor}
            hrTheme={hrLight}
            key={key}
            onButtonClick={handleButtonClick}
            onClose={handleClose}
            visible={true}
        />
    );
}
